import { formatDuration } from '@moussenaires/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiClientError } from '../api/client.js';
import { rosterApi } from '../api/roster.js';
import { useToast } from '../context/ToastContext.js';
import './RosterSyncButton.css';

function msUntil(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Date.parse(iso) - Date.now());
}

/**
 * Top-left, open to every visitor: triggers a full-roster resync. Limited by
 * one global cooldown (not per-user, not per-IP) whose countdown is pushed
 * live over the WebSocket the moment anyone triggers it — see
 * `useRealtime`'s `roster.syncStarted` handler, which writes straight into
 * this same `['roster', 'syncStatus']` query, so every open tab counts down
 * together regardless of who clicked.
 */
export function RosterSyncButton() {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: ['roster', 'syncStatus'],
    queryFn: rosterApi.syncStatus,
    // Kept live over the WebSocket from here — polling would just race it.
    staleTime: Infinity,
  });
  const cooldownUntil = statusQuery.data?.cooldownUntil ?? null;

  // The query itself only changes on the *next* trigger — this is what
  // actually ticks the countdown down, once a second, in between.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!cooldownUntil) return;
    const interval = window.setInterval(() => {
      if (Date.parse(cooldownUntil) <= Date.now()) window.clearInterval(interval);
      forceTick((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [cooldownUntil]);

  const msLeft = msUntil(cooldownUntil);
  const isCoolingDown = msLeft > 0;
  const timeLeft = formatDuration(Math.ceil(msLeft / 1000));

  const syncAll = useMutation({
    mutationFn: () => rosterApi.syncAll(),
    onSuccess: (data) => {
      queryClient.setQueryData(['roster', 'syncStatus'], { cooldownUntil: data.cooldownUntil });
    },
    onError: (error: unknown) => {
      // A 429 means someone else's trigger won the race after our own
      // status check — its cooldown is right there in the error, so there's
      // a countdown to show instead of just an error toast.
      const raceCooldown =
        error instanceof ApiClientError && typeof error.meta?.cooldownUntil === 'string'
          ? error.meta.cooldownUntil
          : null;
      if (raceCooldown) {
        queryClient.setQueryData(['roster', 'syncStatus'], { cooldownUntil: raceCooldown });
        return;
      }
      pushToast({
        id: 'sync-all-error',
        kind: 'error',
        literal:
          error instanceof ApiClientError ? t(`errors.${error.code}`) : t('common.somethingWrong'),
      });
    },
  });

  const disabled = isCoolingDown || syncAll.isPending || statusQuery.isLoading;
  const label = isCoolingDown
    ? t('roster.syncAllCooldown', { time: timeLeft })
    : t('roster.syncAll');

  return (
    <button
      type="button"
      className="roster-sync-button"
      onClick={() => syncAll.mutate()}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <RefreshCw size={16} className={syncAll.isPending ? 'roster-sync-button-spinning' : ''} />
      {isCoolingDown && <span className="roster-sync-button-timer">{timeLeft}</span>}
    </button>
  );
}
